import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  queryOptions,
} from '@tanstack/react-query';
import {
  getLocationsList,
  createLocation,
  getLocation,
  updateLocation,
  deleteLocation,
  fetchLocationInfo,
  getLocationAuthTypes,
  createLocationAuthType,
} from './locations.service';
import {
  UpdateLocationBody,
  GetLocationsParams,
  AuthType,
} from './locations.types';
import { Location } from './locations.types';

// Query keys factory
export const locationsQueryKeys = {
  all: ['locations'] as const,
  list: (id: string | null | undefined, params: GetLocationsParams) => [
    ...locationsQueryKeys.all,
    'list',
    id,
    params.page,
    params.perPage,
    params.sort,
    params.order,
    params.filter,
  ],
  location: (id: string) => [...locationsQueryKeys.all, 'detail', id] as const,
};

// Get locations list query
const getLocationsListQuery = (
  params: GetLocationsParams & {
    id?: string | null | undefined;
    enabled?: boolean;
  },
) => {
  const { id, ...rest } = params;
  const queryKey = locationsQueryKeys.list(id, rest);

  return queryOptions({
    queryKey,
    queryFn: () => getLocationsList(params),
    staleTime: 30 * 1000, // 30 seconds
    refetchOnWindowFocus: false,
    gcTime: 5 * 60 * 1000, // 5 minutes
    placeholderData: keepPreviousData,
  });
};

// Hook to get locations list
export const useGetLocations = (
  id: string | null | undefined,
  params: GetLocationsParams,
) => {
  return useQuery({
    ...getLocationsListQuery({ id, ...params }),
    select: (data) => data.data,
  });
};

// Hook to get single location
export const useGetLocation = (id: string | null | undefined) => {
  return useQuery({
    queryKey: locationsQueryKeys.location(id || ''),
    queryFn: () => getLocation(id!),
    enabled: !!id,
    staleTime: 30 * 1000, // 30 seconds
    refetchOnWindowFocus: false,
    gcTime: 5 * 60 * 1000, // 5 minutes
    select: (data) => data.data,
  });
};

// Create location mutation
export const useCreateLocation = ({
  onSuccess,
  onError,
}: {
  onSuccess?: (data: Location) => void;
  onError?: (error: unknown) => void;
}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createLocation,
    onSuccess: (data) => {
      // Invalidate and refetch locations list
      queryClient.invalidateQueries({
        queryKey: locationsQueryKeys.all,
      });
      onSuccess?.(data.data);
    },
    onError: (error) => {
      console.error('Error creating location:', error);
      onError?.(error);
    },
  });
};

// Update location mutation
export const useUpdateLocation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateLocationBody }) =>
      updateLocation(id, data),
    onSuccess: (data, variables) => {
      // Update the specific location in cache
      queryClient.setQueryData(locationsQueryKeys.location(variables.id), data);

      // Invalidate locations list to refresh
      queryClient.invalidateQueries({
        queryKey: locationsQueryKeys.all,
      });
    },
    onError: (error) => {
      console.error('Error updating location:', error);
    },
  });
};

// Delete location mutation
export const useDeleteLocation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteLocation,
    onSuccess: (_data, locationId) => {
      // Remove the location from cache
      queryClient.removeQueries({
        queryKey: locationsQueryKeys.location(locationId),
      });

      // Invalidate locations list to refresh
      queryClient.invalidateQueries({
        queryKey: locationsQueryKeys.all,
      });
    },
    onError: (error) => {
      console.error('Error deleting location:', error);
    },
  });
};

// Hook to get locations data with loading and error states
export const useLocationsData = (
  id: string | null | undefined,
  params: GetLocationsParams,
) => {
  const {
    data: response,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    ...getLocationsListQuery({ id, ...params }),
  });

  return {
    locations: response?.data || [],
    total: response?.pageInfo?.totalItems || 0,
    pageInfo: response?.pageInfo,
    isLoading,
    isError,
    error,
    refetch,
  };
};

// Hook for fetching location info using authentication
export const useFetchLocationInfo = () => {
  return useMutation({
    mutationFn: ({
      authType,
      locationId,
    }: {
      authType: AuthType;
      locationId?: string;
    }) => fetchLocationInfo(authType, locationId),
    onError: (error) => {
      console.error('Failed to fetch location info:', error);
    },
  });
};

export const useGetLocationAuthTypes = () => {
  return useMutation({
    mutationFn: getLocationAuthTypes,
    onError: (error) => {
      console.error('Failed to fetch location auth types:', error);
    },
  });
};

export const useCreateLocationAuthType = ({
  onSuccess,
  onError,
}: {
  onSuccess?: (data: AuthType) => void;
  onError?: (error: unknown) => void;
}) => {
  return useMutation({
    mutationFn: createLocationAuthType,
    onSuccess: (data) => {
      console.log('data createLocationAuthType', data);
      onSuccess?.(data.data);
    },
    onError: (error) => {
      console.error('Failed to create location auth type:', error);
      onError?.(error);
    },
  });
};
